import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import {
  Users as UsersIcon, Search, Eye, Pencil, Trash2, KeyRound, UserCog,
  Loader2, X, Copy, ShieldCheck, CheckSquare, Square, AlertTriangle,
} from "lucide-react";

// Cross-tenant user table with:
//  • per-row + select-all checkboxes powering bulk deactivate / reactivate / reset password
//  • impersonation with required reason (paper-trailed on the server)
//  • view / edit drawer, delete, and single-user password reset
// Rendered inside PlatformAdmin.jsx under the tenants panel.
export default function SuperAdminUsers() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [drawer, setDrawer] = useState(null); // { user, mode: "view" | "edit" }
  const [editForm, setEditForm] = useState({ name: "", role: "", is_active: true });
  const [resetResult, setResetResult] = useState(null); // { user, password }
  const [bulkResults, setBulkResults] = useState(null); // { action, results }
  const [selected, setSelected] = useState({}); // { [user_id]: true }
  const [bulkBusy, setBulkBusy] = useState(false);
  const [impersonatePrompt, setImpersonatePrompt] = useState(null); // { user, reason }

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/users", { params: { q: q || undefined, limit: 200 } });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setSelected({});
    } catch (err) {
      toast.error("Failed to load users");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
  const allSelected = items.length > 0 && selectedIds.length === items.length;

  const toggleAll = () => {
    if (allSelected) { setSelected({}); return; }
    const next = {};
    items.forEach((u) => { next[u.id] = true; });
    setSelected(next);
  };
  const toggleOne = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  const openView = (u) => { setDrawer({ user: u, mode: "view" }); };
  const openEdit = (u) => {
    setDrawer({ user: u, mode: "edit" });
    setEditForm({ name: u.name || "", role: u.role || "", is_active: u.is_active !== false });
  };
  const saveEdit = async () => {
    setBusyId(drawer.user.id);
    try {
      const r = await api.patch(`/admin/users/${drawer.user.id}`, editForm);
      toast.success("User updated");
      setDrawer(null);
      setItems((arr) => arr.map((x) => x.id === drawer.user.id ? { ...x, ...r.data } : x));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusyId(null); }
  };
  const deleteUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.email}? This cannot be undone.`)) return;
    setBusyId(u.id);
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      setItems((arr) => arr.filter((x) => x.id !== u.id));
      setTotal((n) => Math.max(0, n - 1));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally { setBusyId(null); }
  };
  const resetPassword = async (u) => {
    if (!window.confirm(`Reset password for ${u.email}?\nThey'll need the new password to sign in.`)) return;
    setBusyId(u.id);
    try {
      const r = await api.post(`/admin/users/${u.id}/reset-password`);
      setResetResult({ user: u, password: r.data.new_password });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally { setBusyId(null); }
  };
  const openImpersonate = (u) => setImpersonatePrompt({ user: u, reason: "" });
  const confirmImpersonate = async () => {
    if (!impersonatePrompt) return;
    const { user, reason } = impersonatePrompt;
    if (!reason || reason.trim().length < 3) {
      toast.error("Please add a short reason (min 3 chars)");
      return;
    }
    setBusyId(user.id);
    try {
      const r = await api.post(`/admin/users/${user.id}/impersonate`, { reason: reason.trim() });
      localStorage.setItem("ll_original_token", localStorage.getItem("ll_token") || "");
      localStorage.setItem("ll_original_user", localStorage.getItem("ll_user") || "");
      localStorage.setItem("ll_original_tenant", localStorage.getItem("ll_tenant") || "");
      localStorage.setItem("ll_token", r.data.token);
      localStorage.setItem("ll_user", JSON.stringify(r.data.user));
      localStorage.setItem("ll_tenant", JSON.stringify(r.data.tenant));
      window.location.href = r.data.user.role === "client" ? "/client" : "/app/dashboard";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impersonate failed");
      setBusyId(null);
    }
  };

  const runBulk = async (action) => {
    if (selectedIds.length === 0) return;
    const label = action === "deactivate" ? "deactivate" : action === "reactivate" ? "reactivate" : "reset the password on";
    if (!window.confirm(`Are you sure you want to ${label} ${selectedIds.length} user${selectedIds.length === 1 ? "" : "s"}?`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/users/bulk-action", { action, user_ids: selectedIds });
      setBulkResults(data);
      toast.success(`${data.ok_count}/${data.total} succeeded`);
      // Refresh row state
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk action failed");
    } finally { setBulkBusy(false); }
  };

  return (
    <div className="ll-card-flat overflow-hidden mt-6" data-testid="superadmin-users-card">
      <div className="px-5 py-3 border-b flex flex-wrap items-center justify-between gap-3" style={{ borderColor: "var(--ll-border)" }}>
        <div className="flex items-center gap-2">
          <UsersIcon size={14} />
          <div className="font-display font-bold">All users</div>
          <div className="text-xs text-[var(--ll-ink-soft)]">{total.toLocaleString()} total</div>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex items-center gap-2">
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--ll-ink-muted)]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search email or name…"
              data-testid="sa-users-search"
              className="pl-7 pr-3 py-1.5 border text-xs w-64"
              style={{ borderColor: "var(--ll-border)" }}
            />
          </div>
          <button type="submit" data-testid="sa-users-search-btn" className="text-xs px-3 py-1.5 border hover:bg-[var(--ll-mint)]" style={{ borderColor: "var(--ll-border)" }}>
            Search
          </button>
        </form>
      </div>

      {/* Bulk actions bar — appears only when at least one row is selected */}
      {selectedIds.length > 0 && (
        <div
          className="px-5 py-2 border-b flex flex-wrap items-center justify-between gap-3"
          style={{ borderColor: "var(--ll-border)", background: "var(--ll-mint)" }}
          data-testid="sa-bulk-bar"
        >
          <div className="text-xs">
            <b data-testid="sa-bulk-count">{selectedIds.length}</b> user{selectedIds.length === 1 ? "" : "s"} selected
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => runBulk("deactivate")}
              disabled={bulkBusy}
              data-testid="sa-bulk-deactivate"
              className="text-xs px-3 py-1.5 border hover:bg-white flex items-center gap-1 disabled:opacity-50"
              style={{ borderColor: "var(--ll-border)" }}
            >
              {bulkBusy ? <Loader2 size={11} className="animate-spin" /> : <AlertTriangle size={11} />} Deactivate
            </button>
            <button
              onClick={() => runBulk("reactivate")}
              disabled={bulkBusy}
              data-testid="sa-bulk-reactivate"
              className="text-xs px-3 py-1.5 border hover:bg-white flex items-center gap-1 disabled:opacity-50"
              style={{ borderColor: "var(--ll-border)" }}
            >
              Reactivate
            </button>
            <button
              onClick={() => runBulk("reset_password")}
              disabled={bulkBusy}
              data-testid="sa-bulk-reset"
              className="text-xs px-3 py-1.5 border hover:bg-white flex items-center gap-1 disabled:opacity-50"
              style={{ borderColor: "var(--ll-border)" }}
            >
              <KeyRound size={11} /> Reset passwords
            </button>
            <button
              onClick={() => setSelected({})}
              className="text-xs px-3 py-1.5 hover:underline"
              data-testid="sa-bulk-clear"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="p-10 text-center text-sm text-[var(--ll-ink-soft)]">
          <Loader2 size={16} className="mx-auto animate-spin mb-2" /> Loading…
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] bg-[var(--ll-bg-sidebar)] border-b" style={{ borderColor: "var(--ll-border)" }}>
              <th className="px-4 py-2 font-medium w-8">
                <button
                  type="button"
                  onClick={toggleAll}
                  className="p-0.5 hover:bg-[var(--ll-mint)]"
                  aria-label="Select all"
                  data-testid="sa-select-all"
                  title={allSelected ? "Deselect all" : "Select all"}
                >
                  {allSelected ? <CheckSquare size={13} /> : <Square size={13} />}
                </button>
              </th>
              <th className="px-4 py-2 font-medium">Email</th>
              <th className="px-4 py-2 font-medium">Name</th>
              <th className="px-4 py-2 font-medium">Role</th>
              <th className="px-4 py-2 font-medium">Tenant</th>
              <th className="px-4 py-2 font-medium">Joined</th>
              <th className="px-4 py-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="sa-users-tbody">
            {items.map((u) => (
              <tr key={u.id} className="border-b hover:bg-[var(--ll-bg-sidebar)]" style={{ borderColor: "var(--ll-border)" }} data-testid={`sa-user-row-${u.id}`}>
                <td className="px-4 py-2">
                  <button
                    type="button"
                    onClick={() => toggleOne(u.id)}
                    className="p-0.5 hover:bg-[var(--ll-mint)]"
                    aria-label={`Select ${u.email}`}
                    data-testid={`sa-select-${u.id}`}
                  >
                    {selected[u.id] ? <CheckSquare size={13} /> : <Square size={13} />}
                  </button>
                </td>
                <td className="px-4 py-2 text-xs">
                  {u.email}
                  {u.is_platform_admin && <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] px-1 py-0.5" style={{ background: "#FEF3C7", color: "#92400E" }}><ShieldCheck size={10} /> admin</span>}
                  {u.is_active === false && <span className="ml-1 text-[10px] px-1 py-0.5" style={{ background: "#FEE2E2", color: "#991B1B" }}>inactive</span>}
                </td>
                <td className="px-4 py-2 text-xs">{u.name}</td>
                <td className="px-4 py-2 text-[10px] uppercase tracking-widest">{u.role}</td>
                <td className="px-4 py-2 text-xs text-[var(--ll-ink-soft)]">{u.tenant_name}</td>
                <td className="px-4 py-2 text-[11px] text-[var(--ll-ink-soft)]">{fmtDate(u.created_at)}</td>
                <td className="px-4 py-2 text-right">
                  <div className="inline-flex items-center gap-1">
                    <IconBtn onClick={() => openView(u)} icon={Eye} label="View" testid={`sa-view-${u.id}`} />
                    <IconBtn onClick={() => openEdit(u)} icon={Pencil} label="Edit" testid={`sa-edit-${u.id}`} />
                    <IconBtn onClick={() => resetPassword(u)} icon={KeyRound} label="Reset password" testid={`sa-reset-${u.id}`} busy={busyId === u.id} />
                    <IconBtn onClick={() => openImpersonate(u)} icon={UserCog} label="Impersonate" testid={`sa-imp-${u.id}`} busy={busyId === u.id} />
                    <IconBtn onClick={() => deleteUser(u)} icon={Trash2} label="Delete" testid={`sa-del-${u.id}`} tone="danger" busy={busyId === u.id} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* View / Edit drawer */}
      {drawer && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex justify-end"
          onClick={() => setDrawer(null)}
          data-testid="sa-user-drawer"
        >
          <div onClick={(e) => e.stopPropagation()} className="bg-white h-full w-full max-w-md shadow-xl overflow-y-auto">
            <div className="px-5 py-3 border-b flex items-center justify-between bg-[var(--ll-ink)] text-white" style={{ borderColor: "var(--ll-border)" }}>
              <div>
                <div className="text-[10px] uppercase tracking-widest opacity-75">{drawer.mode === "edit" ? "Edit user" : "View user"}</div>
                <div className="font-display font-bold text-sm">{drawer.user.email}</div>
              </div>
              <button onClick={() => setDrawer(null)} aria-label="Close" className="text-white/80 hover:text-white"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              {drawer.mode === "view" ? (
                <>
                  <Row k="Name" v={drawer.user.name} />
                  <Row k="Role" v={drawer.user.role?.toUpperCase()} />
                  <Row k="Active" v={drawer.user.is_active === false ? "No" : "Yes"} />
                  <Row k="Platform admin" v={drawer.user.is_platform_admin ? "Yes" : "No"} />
                  <Row k="Tenant" v={drawer.user.tenant_name} />
                  <Row k="Plan" v={drawer.user.tenant_plan} />
                  <Row k="Subscription" v={drawer.user.tenant_status} />
                  <Row k="Joined" v={fmtDate(drawer.user.created_at)} />
                  <Row k="Last login" v={fmtDate(drawer.user.last_login_at)} />
                </>
              ) : (
                <>
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Name</span>
                    <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="sa-edit-name" className="mt-1 w-full px-3 py-2 border text-sm" style={{ borderColor: "var(--ll-border)" }} />
                  </label>
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Role</span>
                    <select value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} data-testid="sa-edit-role" className="mt-1 w-full px-3 py-2 border bg-white text-sm" style={{ borderColor: "var(--ll-border)" }}>
                      <option value="owner">owner</option>
                      <option value="admin">admin</option>
                      <option value="accountant">accountant</option>
                      <option value="viewer">viewer</option>
                      <option value="client">client</option>
                    </select>
                  </label>
                  <label className="flex items-center gap-2 mt-2">
                    <input type="checkbox" checked={editForm.is_active} onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })} data-testid="sa-edit-active" />
                    <span>Active</span>
                  </label>
                  <div className="pt-3 flex items-center justify-end gap-2">
                    <button onClick={() => setDrawer(null)} className="px-4 py-2 border text-sm" style={{ borderColor: "var(--ll-border)" }}>Cancel</button>
                    <button onClick={saveEdit} disabled={busyId === drawer.user.id} data-testid="sa-edit-save" className="px-4 py-2 bg-[var(--ll-ink)] text-white hover:bg-[var(--ll-ink-hover)] text-sm disabled:opacity-50">
                      Save changes
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Impersonation reason prompt */}
      {impersonatePrompt && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setImpersonatePrompt(null)} data-testid="sa-imp-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white border w-full max-w-md p-6" style={{ borderColor: "var(--ll-ink)" }}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h2 className="font-display font-bold text-xl flex items-center gap-2"><UserCog size={16} /> Impersonate user</h2>
                <p className="text-xs text-[var(--ll-ink-soft)] mt-1">Everything you do during the 15-minute session is logged against your account and this reason.</p>
              </div>
              <button onClick={() => setImpersonatePrompt(null)} aria-label="Close" className="text-[var(--ll-ink-soft)]"><X size={16} /></button>
            </div>
            <div className="mb-3 text-sm">
              <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Target</div>
              <div className="font-medium">{impersonatePrompt.user.email}</div>
              <div className="text-xs text-[var(--ll-ink-soft)]">{impersonatePrompt.user.tenant_name}</div>
            </div>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Reason (required)</span>
              <textarea
                value={impersonatePrompt.reason}
                onChange={(e) => setImpersonatePrompt({ ...impersonatePrompt, reason: e.target.value })}
                placeholder="e.g. Support ticket #4231 — reproducing a report export bug"
                data-testid="sa-imp-reason"
                rows={3}
                className="mt-1 w-full px-3 py-2 border text-sm"
                style={{ borderColor: "var(--ll-border)" }}
                autoFocus
              />
            </label>
            <div className="pt-3 flex items-center justify-end gap-2">
              <button onClick={() => setImpersonatePrompt(null)} className="px-4 py-2 border text-sm" style={{ borderColor: "var(--ll-border)" }}>Cancel</button>
              <button
                onClick={confirmImpersonate}
                disabled={busyId === impersonatePrompt.user.id}
                data-testid="sa-imp-confirm"
                className="px-4 py-2 bg-[var(--ll-ink)] text-white hover:bg-[var(--ll-ink-hover)] text-sm disabled:opacity-50"
              >
                Start 15-min session
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk action results modal */}
      {bulkResults && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setBulkResults(null)} data-testid="sa-bulk-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white border w-full max-w-lg p-6" style={{ borderColor: "var(--ll-ink)" }}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h2 className="font-display font-bold text-xl">Bulk {bulkResults.action.replace("_", " ")} results</h2>
                <p className="text-xs text-[var(--ll-ink-soft)] mt-1">
                  {bulkResults.ok_count} of {bulkResults.total} succeeded.
                </p>
              </div>
              <button onClick={() => setBulkResults(null)} aria-label="Close" className="text-[var(--ll-ink-soft)]"><X size={16} /></button>
            </div>
            <div className="max-h-80 overflow-y-auto border" style={{ borderColor: "var(--ll-border)" }}>
              <table className="w-full text-xs">
                <tbody>
                  {bulkResults.results.map((r) => (
                    <tr key={r.user_id} className="border-b" style={{ borderColor: "var(--ll-border)" }}>
                      <td className="px-3 py-2">{r.email || r.user_id.slice(0, 8)}</td>
                      <td className="px-3 py-2">
                        {r.ok ? (
                          <span className="text-green-700">✓ ok</span>
                        ) : (
                          <span className="text-red-700">✗ {r.error}</span>
                        )}
                      </td>
                      {bulkResults.action === "reset_password" && r.new_password && (
                        <td className="px-3 py-2 font-mono text-[11px]">
                          <span className="mr-2">{r.new_password}</span>
                          <button onClick={() => { navigator.clipboard?.writeText(r.new_password); toast.success("Copied"); }} className="text-[10px] px-1.5 py-0.5 border hover:bg-[var(--ll-mint)]" style={{ borderColor: "var(--ll-border)" }}>
                            <Copy size={9} className="inline" /> copy
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 text-right">
              <button onClick={() => setBulkResults(null)} className="px-4 py-2 bg-[var(--ll-ink)] text-white text-sm">Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Password reset result modal */}
      {resetResult && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setResetResult(null)} data-testid="sa-reset-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white border w-full max-w-md p-6" style={{ borderColor: "var(--ll-ink)" }}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h2 className="font-display font-bold text-xl flex items-center gap-2"><KeyRound size={16} /> Temporary password</h2>
                <p className="text-xs text-[var(--ll-ink-soft)] mt-1">Share with {resetResult.user.email} out-of-band. This is shown once.</p>
              </div>
              <button onClick={() => setResetResult(null)} aria-label="Close" className="text-[var(--ll-ink-soft)]"><X size={16} /></button>
            </div>
            <div className="border p-3 font-mono text-sm flex items-center justify-between" style={{ borderColor: "var(--ll-border)", background: "var(--ll-bg-sidebar)" }}>
              <span data-testid="sa-reset-password">{resetResult.password}</span>
              <button
                onClick={() => { navigator.clipboard?.writeText(resetResult.password); toast.success("Copied"); }}
                data-testid="sa-reset-copy"
                className="text-xs px-2 py-1 border hover:bg-white flex items-center gap-1"
                style={{ borderColor: "var(--ll-border)" }}
              >
                <Copy size={11} /> Copy
              </button>
            </div>
            <div className="mt-4 text-xs text-[var(--ll-ink-soft)]">
              The user should sign in with this password and change it from their profile.
            </div>
            <div className="mt-4 text-right">
              <button onClick={() => setResetResult(null)} className="px-4 py-2 bg-[var(--ll-ink)] text-white text-sm">Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function IconBtn({ onClick, icon: Icon, label, testid, tone, busy }) {
  const isDanger = tone === "danger";
  return (
    <button
      onClick={onClick}
      disabled={busy}
      data-testid={testid}
      title={label}
      aria-label={label}
      className={`p-1.5 border hover:bg-[var(--ll-mint)] disabled:opacity-50 ${isDanger ? "hover:bg-[#FEE2E2] hover:text-[#991B1B]" : ""}`}
      style={{ borderColor: "var(--ll-border)" }}
    >
      {busy ? <Loader2 size={12} className="animate-spin" /> : <Icon size={12} />}
    </button>
  );
}

function Row({ k, v }) {
  return (
    <div className="grid grid-cols-2 gap-3 py-1 border-b" style={{ borderColor: "var(--ll-border)" }}>
      <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">{k}</div>
      <div className="text-sm">{v || "—"}</div>
    </div>
  );
}
